import { Controller } from "@hotwired/stimulus"

// The ⌘K palette. ⌘K (Ctrl+K) opens it from anywhere, / when not typing; the results come from the
// server as HTML. Up and down move through them, Enter opens the one picked.
export default class extends Controller {
  static targets = ["input", "results", "option"]
  static values = { url: String }

  connect() {
    this.closeBeforeCache = () => this.close()
    document.addEventListener("turbo:before-cache", this.closeBeforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.closeBeforeCache)
    clearTimeout(this.timer)
  }

  hotkey(event) {
    const commandK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k"
    const slash = event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey && !this.typing(event.target)
    if (!commandK && !slash) return
    if (slash && document.documentElement.hasAttribute("data-drawer-open")) return

    event.preventDefault()
    this.element.open ? this.close() : this.open()
  }

  open() {
    if (this.element.open) return
    this.element.showModal()
    this.inputTarget.focus()
    this.load()
  }

  close() {
    if (this.element.open) this.element.close()
  }

  reset() {
    this.inputTarget.value = ""
    this.resultsTarget.innerHTML = ""
  }

  backdropClick(event) {
    if (event.target === this.element) this.close()
  }

  search() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.load(), 120)
  }

  async load() {
    const query = this.inputTarget.value.trim()
    const url = `${this.urlValue}?${new URLSearchParams({ q: query })}`
    this.request?.abort()
    this.request = new AbortController()
    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, signal: this.request.signal })
      if (!response.ok || this.inputTarget.value.trim() !== query) return
      this.resultsTarget.innerHTML = await response.text()
      this.select(0)
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  navigate(event) {
    const options = this.optionTargets
    const current = options.findIndex((option) => option.getAttribute("aria-selected") === "true")
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (options.length === 0) return
      const step = event.key === "ArrowDown" ? 1 : -1
      this.select((current + step + options.length) % options.length)
    } else if (event.key === "Enter" && options[current]) {
      event.preventDefault()
      options[current].click()
    }
  }

  hover(event) {
    this.select(this.optionTargets.indexOf(event.currentTarget), { scroll: false })
  }

  select(index, { scroll = true } = {}) {
    this.optionTargets.forEach((option, i) => option.setAttribute("aria-selected", i === index ? "true" : "false"))
    const selected = this.optionTargets[index]
    if (!selected) return
    this.inputTarget.setAttribute("aria-activedescendant", selected.id || "")
    if (scroll) selected.scrollIntoView({ block: "nearest" })
  }

  optionTargetConnected(option) {
    option.addEventListener("click", () => this.close(), { once: true })
  }

  typing(element) {
    if (element.tagName === "INPUT") return !["checkbox", "radio", "button", "submit"].includes(element.type)
    return element.isContentEditable || ["TEXTAREA", "SELECT"].includes(element.tagName)
  }
}
